import { generateId } from '@freelancer/datastore/testing/helpers';
import type { CustomerWebhook } from '../customer-webhooks';
import type {
  AddressResponse,
  CustomerAccountType,
  CustomerTaxNumberResponse,
  CustomerVerificationResponse,
} from './customers.backend-model';
import type {
  Customer,
  CustomerCompany,
  CustomerElectronicVerificationStatus,
  CustomerEmailVerification,
  DisbursementMethod,
} from './customers.model';

export interface GenerateCustomerOptions {
  readonly id?: number;
  readonly accountType?: CustomerAccountType;
  readonly address?: AddressResponse;
  readonly company?: CustomerCompany;
  readonly customerEmailVerification?: CustomerEmailVerification;
  readonly dateOfBirth?: number;
  readonly disbursementMethods?: readonly DisbursementMethod[];
  readonly displayName?: string;
  readonly electronicVerification?: CustomerElectronicVerificationStatus;
  readonly email?: string;
  readonly firstName?: string;
  readonly lastName?: string;
  readonly middleName?: string;
  readonly phoneNumber?: string;
  readonly shippingAddress?: AddressResponse;
  readonly taxNumbers?: readonly CustomerTaxNumberResponse[];
  readonly username?: string;
  readonly verification?: CustomerVerificationResponse;
  readonly webhooks?: readonly CustomerWebhook[];
}

export function generateCustomerObject(
  {
    id,
    accountType,
    address,
    company,
    customerEmailVerification,
    dateOfBirth,
    disbursementMethods,
    displayName,
    electronicVerification,
    email,
    firstName,
    lastName,
    middleName,
    phoneNumber,
    shippingAddress,
    taxNumbers,
    username,
    verification,
    webhooks,
  }: GenerateCustomerOptions = {
    email: 'test@example.com',
    verification: notVerified(),
    customerEmailVerification: {
      verified: false,
    },
    electronicVerification: {
      available: false,
      documentsSupported: [],
    },
  },
): Customer {
  return {
    id: id ?? generateId(),
    accountType: accountType ?? 'individual',
    address,
    company,
    customerEmailVerification: customerEmailVerification ?? {
      verified: false,
    },
    dateOfBirth,
    disbursementMethods,
    displayName,
    electronicVerification: electronicVerification ?? {
      available: false,
      documentsSupported: [],
    },
    email: email ?? 'uitest@escrow.com',
    firstName,
    lastName,
    middleName,
    phoneNumber,
    shippingAddress,
    taxNumbers,
    username,
    verification: verification ?? notVerified(),
    webhooks,
  };
}

function notVerified(): CustomerVerificationResponse {
  return {
    personal: {
      status: 'not_verified',
    },
    company: {
      status: 'not_verified',
    },
    extended: {
      status: 'not_verified',
    },
  };
}

export function createDisbursementMethods(): readonly DisbursementMethod[] {
  return [
    {
      accountName: 'Test User',
      bankAccountNumber: '0389174625',
      bankAddress: {
        country: 'PH',
      },
      bankName: 'BPI',
      bankSwiftCode: 'TLBPPHMMXXX',
      currency: 'usd',
      id: 1_718_211,
      type: 'wire_transfer',
    },
    {
      accountName: 'Test User',
      bankAccountNumber: '10483927',
      bankAddress: {
        country: 'AU',
      },
      bankBranchNumber: '062345',
      bankName: 'Commonwealth Bank of Australia',
      bankSortCode: '062345',
      bankSwiftCode: 'CTBAAU2SXXX',
      currency: 'aud',
      id: 1_718_219,
      type: 'wire_transfer',
    },
    {
      accountName: 'Test User',
      accountType: 'savings',
      bankAbaRoutingNumber: '084726351',
      bankAccountNumber: '900174628519',
      bankAddress: {
        country: 'US',
      },
      bankName: 'Redwood National Bank',
      currency: 'usd',
      id: 1_718_220,
      type: 'ach',
    },
    {
      accountName: 'Test User',
      bankAbaRoutingNumber: '114923756',
      bankAccountNumber: '628490173552',
      bankAddress: {
        country: 'US',
      },
      bankName: 'Silverline Community Bank',
      currency: 'usd',
      id: 1_718_230,
      type: 'wire_transfer',
    },
    {
      accountName: 'Test User',
      bankAccountNumber: '58394726',
      bankAddress: {
        country: 'GB',
      },
      bankBranchNumber: '407219',
      bankIban: 'GB82ATBK40721958394726',
      bankName: 'Albion Trust Bank',
      bankSortCode: '407219',
      bankSwiftCode: 'ATBKGB2L',
      currency: 'gbp',
      id: 1_718_244,
      type: 'wire_transfer',
    },
    {
      accountName: 'Test User',
      bankAccountNumber: '091234567890',
      bankAddress: {
        country: 'PH',
      },
      bankName: 'PH Euro Bank',
      bankSwiftCode: 'DEMOPHMMXXX',
      currency: 'euro',
      id: 1_718_317,
      type: 'wire_transfer_international',
    },
  ];
}
